import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations.ts';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFundsForOrganization } from '@dealstack/frontend/shared/dataExchange/services/fund.service.ts';
import { CreateFundDrawer } from '@dealstack/frontend/app/pages/Organization/OrganizationFunds/components/CreateFund/CreateFundDrawer.tsx';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { FundWithDealRes } from '@dealstack/shared/lib/generated';
import { getFundsTableColumns } from './fundsTableColumns';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';
import { Button, Flex } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';

const EMPTY_FUNDS: FundWithDealRes[] = [];

interface Props {
	organizationId: string;
	onViewLinkedDeals: (fund: FundWithDealRes) => void;
}

export const FundsTable = ({ organizationId, onViewLinkedDeals }: Props) => {
	const tOrganization = useTranslationOrganization();
	const [showCreateDrawer, setShowCreateDrawer] = useState(false);
	const [fundToEditId, setFundToEditId] = useState<string | null>(null);

	const { isFetching, data, refetch } = useQuery({
		queryKey: ['getFundsForOrganization', { organizationId }],
		queryFn: ({ signal }) => getFundsForOrganization({ organizationId }, signal),
	});

	const fundToEdit = useMemo(
		() => data?.find((fund) => fund.id === fundToEditId) ?? null,
		[data, fundToEditId]
	);

	const handleEditFund = useCallback((fund: FundWithDealRes) => {
		setFundToEditId(fund.id);
		setShowCreateDrawer(true);
	}, []);

	const handleCloseDrawer = useCallback(() => {
		setShowCreateDrawer(false);
		setFundToEditId(null);
	}, []);

	const columns = useMemo(
		() =>
			getFundsTableColumns(tOrganization, {
				onViewLinkedDeals,
				onEditFund: handleEditFund,
			}),
		[tOrganization, onViewLinkedDeals, handleEditFund]
	);
	const dataOperations = useMantineTableDataOperations();

	return (
		<Flex direction="column" gap="1rem">
			<Flex justify="flex-end">
				<Button
					leftSection={<Icon variant="add" size="lg" />}
					onClick={() => setShowCreateDrawer(true)}
				>
					{tOrganization('funds.create_fund')}
				</Button>
			</Flex>
			<Flex direction="column" w="100%" mih={0} style={{ flex: 1, overflow: 'hidden' }}>
				<MantineTable
					columns={columns}
					data={data ?? EMPTY_FUNDS}
					dataOperations={dataOperations}
					isLoading={isFetching}
					manualFiltering={false}
					manualSorting={false}
					mantineTableBodyRowProps={({ row }) => ({
						id: row.id,
						'data-testid': makeComplexDataTestId(
							TEST_ELEMENT_IDS.FUND_TABLE_ROW,
							row.original.deal.name
						),
					})}
				/>
			</Flex>
			<CreateFundDrawer
				opened={showCreateDrawer}
				onClose={handleCloseDrawer}
				onSuccess={() => refetch()}
				fund={fundToEdit ?? undefined}
				organizationId={organizationId}
			/>
		</Flex>
	);
};
